import type { StatementView, SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { statementKindKeys } from './billing';
import { decimalAmount } from './billingReport';
import { csvTextCell } from './csv';

/**
 * The statement as CSV in the reader's language, with a byte-order mark so spreadsheets read the
 * accents. It opens with a manifest of what the figures cover (account, day, currency and when it was
 * generated); every free text goes through the formula-injection guard and amounts keep a point as
 * decimal separator. Amounts are in the account's own currency and are never converted or summed.
 */
export function statementCsv(
  statement: StatementView,
  locale: SupportedLocale,
  generatedAt: string,
): string {
  const text = (value: string): string => csvTextCell(value);
  const label = (key: TranslationKey): string => text(translate(locale, key));
  const row = (cells: readonly string[]): string => cells.join(',');
  const lines = [
    row([label('billing.statement.title')]),
    row([label('billing.reports.account'), text(statement.label)]),
    row([label('billing.statement.asOf'), text(statement.asOf)]),
    row([label('billing.reports.generatedAt'), text(generatedAt)]),
    row([label('billing.reports.currency'), text(statement.currency)]),
    row([
      label('billing.position.overdue'),
      decimalAmount(statement.position.overdueMinor),
      label('billing.position.upcoming'),
      decimalAmount(statement.position.upcomingMinor),
      label('billing.position.credit'),
      decimalAmount(statement.position.unallocatedCreditMinor),
    ]),
    '',
    row([
      label('billing.col.date'),
      label('billing.col.type'),
      label('billing.col.description'),
      label('billing.col.effectiveOn'),
      label('billing.col.change'),
      label('billing.col.balance'),
    ]),
  ];
  if (statement.truncated) {
    lines.push(
      row([
        '',
        '',
        label('billing.statement.opening'),
        '',
        '',
        decimalAmount(statement.openingBalanceMinor),
      ]),
    );
  }
  for (const line of statement.lines) {
    lines.push(
      row([
        text(line.postedOn),
        text(translate(locale, statementKindKeys[line.kind])),
        text(line.description ?? ''),
        text(line.effectiveOn ?? ''),
        decimalAmount(line.effectMinor),
        decimalAmount(line.balanceMinor),
      ]),
    );
  }
  lines.push(
    row([
      '',
      '',
      label('billing.statement.closing'),
      '',
      '',
      decimalAmount(statement.closingBalanceMinor),
    ]),
  );
  if (statement.openCharges.length > 0) {
    lines.push(
      '',
      row([label('billing.statement.openCharges')]),
      row([
        label('billing.col.dueOn'),
        label('billing.col.description'),
        label('billing.col.outstanding'),
      ]),
    );
    for (const charge of statement.openCharges) {
      lines.push(
        row([text(charge.dueOn), text(charge.description), decimalAmount(charge.outstandingMinor)]),
      );
    }
  }
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
